import { PageName, PAGES } from "../constants/Pages";
import { NEW_POST_ID, POST_STATES, POST_TYPES } from "../constants/PostsView";
import { ONLY_MANUAL_PLATFORMS } from "../constants/Platforms";
import "@dialogueconsulting/sked-preview/dist/style.css";
import { SkedCorePostModel } from "@dialogueconsulting/sked-preview";
import cloneDeep from "lodash/cloneDeep";
import isEmpty from "lodash/isEmpty";
import { isValid } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import Bugsnag from "@bugsnag/js";
import { safeParseDateString } from "./date-helpers";
import { getAccountPlatformType } from "../entities/accounts/libs/account-platform";
import {
  getPublishingStatus,
  isRemovableFromQueue,
} from "../features/post/status";
import companySizeData from "../constants/linkedinAudiences/companySize.json";
import jobFunctionsData from "../constants/linkedinAudiences/jobFunctions.json";
import senioritiesData from "../constants/linkedinAudiences/seniorities.json";
import senioritiesDataDeprecated from "../constants/linkedinAudiences/senioritiesDeprecated.json";
import industriesData from "../constants/linkedinAudiences/industries.json";
import {
  fetchLocationLabelById,
  fetchOrganizationLabelById,
} from "../api/linkedinTargetAudience";
import type { Post } from "../features/post/types";
import type { User } from "../features/user";
import { hasTeamwork } from "./feature-gates";
import type {
  AccountPlatformType,
  AccountType,
} from "../shared/types/accounts";
import type { PostType } from "../shared/types/post";
import type { LoadedDraft } from "../selectors/loaded-posts-normalizer";
import { normalizePostMediaShape } from "../features/post/format-post";
import { pickThumbnailFallback } from "./media";

type AudienceOption = { id: string; label: string };
type AudienceMap = Record<string, AudienceOption[]>;
type LegacyAudienceMap = Record<string, unknown[]>;
type PostWithLegacyLinkedInAudiences = Post & {
  liTargetEntities?: LegacyAudienceMap[] | AudienceMap;
};

const isAudienceOption = (value: unknown): value is AudienceOption => {
  return (
    typeof value === "object" &&
    value !== null &&
    "id" in value &&
    typeof value.id === "string" &&
    "label" in value &&
    typeof value.label === "string"
  );
};

const getPostPlatformCode = (
  platformType: Post["platformType"] | undefined
): AccountPlatformType | null => {
  if (!platformType || platformType === "LINKED") {
    return null;
  }

  return platformType;
};

export const getPageVerbs = (page: PageName, post: Post, user: User) => {
  const pageVerbs = {
    previewable: false,
    repostable: false,
    editable: false,
    urlEditable: false,
    queueable: false,
    draftable: false,
    showLegacyEdit: false,
    removeable: false as boolean | undefined,
    removeableFromQueue: false as boolean | undefined,
    notifiable: false,
    duplicatable: false,
  };

  pageVerbs.showLegacyEdit = hasTeamwork(user);

  switch (page) {
    case PAGES.UPCOMING: {
      pageVerbs.editable = true;
      pageVerbs.queueable = true;
      pageVerbs.draftable = true;
      pageVerbs.removeable = true;
      pageVerbs.previewable = true;
      pageVerbs.duplicatable = true;
      pageVerbs.removeableFromQueue = isRemovableFromQueue(post);
      break;
    }
    case PAGES.DRAFT: {
      pageVerbs.queueable = true;
      pageVerbs.editable = true;
      pageVerbs.removeable = true;
      pageVerbs.previewable = true;
      pageVerbs.duplicatable = true;
      break;
    }
    case PAGES.ARCHIVE: {
      const isManual = isManualPost(post);
      const canPostAgain = isManual || !(post.isPosting || post.isQueued);
      const duplicatable =
        post.isSuccess || (!post.userDisplayReason && isManual);
      pageVerbs.repostable = !duplicatable && canPostAgain;
      pageVerbs.urlEditable = true;
      pageVerbs.draftable = canPostAgain;
      pageVerbs.removeableFromQueue = isRemovableFromQueue(post);
      pageVerbs.removeable = post.isCanceled || post.isSuccess;
      pageVerbs.notifiable = true;
      pageVerbs.duplicatable = duplicatable;
      break;
    }
  }

  return pageVerbs;
};

export const isManualPost = (
  post: Pick<Post, "publishStoryManually" | "postOptions" | "platformType">
) =>
  post.publishStoryManually ||
  post?.postOptions?.isNotification ||
  (getPostPlatformCode(post.platformType)
    ? ONLY_MANUAL_PLATFORMS.includes(getPostPlatformCode(post.platformType)!)
    : false);

export const isAutoPublishedRegularStory = (
  post: Pick<Post, "type" | "storyType" | "isStory" | "publishStoryManually">
) =>
  (post.type === POST_TYPES.STORY || post.isStory) &&
  post.storyType !== "reel" &&
  post.storyType !== "igtv" &&
  !post.publishStoryManually;

export const getPageEntity = (page: PageName) => {
  let pageEntity: "post" | "draft" | null = null;
  switch (page) {
    case PAGES.UPCOMING: {
      pageEntity = "post";
      break;
    }
    case PAGES.DRAFT: {
      pageEntity = "draft";
      break;
    }
    case PAGES.ARCHIVE: {
      pageEntity = "post";
      break;
    }
  }
  return pageEntity;
};

export const getPlatformTypes = (
  _post: Post,
  accounts: AccountType[]
): AccountPlatformType[] => {
  const platforms: AccountPlatformType[] = [];
  accounts.forEach((account) => {
    const type = account.platformType || "IG";
    platforms.push(type);
  });
  return [...new Set(platforms)];
};

export const getFormattedPostDate = (
  post: Pick<Post, "when" | "lastRunAt" | "_id" | "id">,
  page: PageName,
  user: Pick<User, "timezone" | "preferences">
): string => {
  let time: Post["when"] | Post["lastRunAt"] | null = null;
  switch (page) {
    case PAGES.UPCOMING:
      time = post.when;
      break;
    case PAGES.DRAFT:
      // Leave time null
      break;
    case PAGES.ARCHIVE:
      time = post.lastRunAt;
      break;
    default:
      throw new Error(`Invalid view type for post time ${page}`);
  }

  let dateFormat = "EEE d MMM, H:mm";
  if (user.preferences && user.preferences.timePreference === "12h") {
    dateFormat = "EEE d MMM, h:mm a";
  }

  // A missing date (null / undefined / empty string) is expected — e.g. an
  // in-flight or legacy post that has no scheduled `when`. Render nothing and do
  // NOT report it; the Bugsnag warning below is reserved for a value that is
  // actually present but unparseable (a real data problem worth investigating).
  if (time == null || time === "") {
    return "";
  }

  // Use safeParseDateString for consistent date parsing across all formats
  // (handles US locale format MM/DD/YYYY from API and Safari compatibility)
  // safeParseDateString returns a UTC Date, so we use formatInTimeZone to display
  // in the user's profile timezone (not browser local timezone) - SKE-2716
  const parsedDate = safeParseDateString(time, user.timezone);

  if (!isValid(parsedDate)) {
    Bugsnag.notify(
      new Error("Invalid post date in getFormattedPostDate"),
      (event) => {
        event.severity = "warning";
        event.addMetadata("postDate", {
          page,
          timezone: user.timezone,
          timeValue: String(time),
          timeType: typeof time,
          postId: post?._id || post?.id,
        });
      }
    );

    return "";
  }

  return formatInTimeZone(parsedDate, user.timezone, dateFormat);
};

/**
 *
 * @param {string} selectedPlatformType
 * @param {Object[]} accounts
 * @returns {Object}
 */
export function getTargetAccountForPostPreviewModel(
  accounts: AccountType[],
  selectedPlatformType: AccountPlatformType | "LINKED",
  preferredAccountId?: string
): AccountType {
  let account: AccountType | undefined;
  const preferredAccount = preferredAccountId
    ? accounts.find((candidate) => candidate._id === preferredAccountId)
    : undefined;

  if (selectedPlatformType === "LINKED") {
    account = preferredAccount;
    // Preserve the legacy default when no explicit preview account is selected.
    if (!account) {
      account = accounts.find((acc) => getAccountPlatformType(acc) === "IG");
    }
  } else {
    if (
      preferredAccount &&
      getAccountPlatformType(preferredAccount) === selectedPlatformType
    ) {
      account = preferredAccount;
    } else {
      account = accounts.find(
        (candidate) =>
          getAccountPlatformType(candidate) === selectedPlatformType
      );
    }
  }
  return account || accounts[0];
}

export const getPostPreviewModel = (
  post: Post | LoadedDraft,
  accounts: AccountType[],
  selectedPlatform: AccountPlatformType | "LINKED",
  preferredAccountId?: string
) => {
  // eslint-disable-next-line unicorn/prefer-structured-clone
  const _post = cloneDeep(post);
  const corePost = isLoadedDraft(_post) ? _post.schedule : _post;
  const account = getTargetAccountForPostPreviewModel(
    accounts,
    selectedPlatform || corePost.platformType || "IG",
    preferredAccountId
  );
  const previewPlatformType = getAccountPlatformType(
    account
  ) as Post["platformType"];

  if (isLoadedDraft(_post)) {
    _post.schedule.platformType = previewPlatformType;
  } else {
    _post.platformType = previewPlatformType;
  }
  // Delete post.platforms - this will mean that sked-preview will always use the platformType defined above.
  if (!isLoadedDraft(_post) && _post.platforms) {
    delete _post.platforms;
  }
  return {
    ...new SkedCorePostModel(_post, account),
    collaboratingIgUsers:
      (corePost.collaboratingIgUsers?.length ?? 0) > 0
        ? [
            {
              username: account.login,
              profilePictureUrl: account.profilePictureUrl,
            },
            ...(corePost.collaboratingIgUsers ?? []),
          ]
        : null,
  };
};

export const getPostState = (post: Post | LoadedDraft) => {
  switch ((post as Post).postType) {
    case "draft": {
      return POST_STATES.DRAFT;
    }
    case "queue": {
      return POST_STATES.QUEUED;
    }
    case "schedule": {
      return POST_STATES.SCHEDULED;
    }
    case "archive": {
      return POST_STATES.ARCHIVED;
    }
  }

  if ((post as LoadedDraft).schedule) {
    return POST_STATES.DRAFT;
  }

  if ((post as Post).queued) {
    return POST_STATES.QUEUED;
  }

  if ((post as Post).nextRunAt) {
    return POST_STATES.SCHEDULED;
  }

  if ((post as Post).lastRunAt) {
    return POST_STATES.ARCHIVED;
  }

  // Maybe a normalized post was passed in.
  return POST_STATES.DRAFT;
};

export const detectPostType = (
  post: Partial<
    Pick<Post, "url" | "isVideo" | "isDocument" | "type" | "isStory"> & {
      mediaFiles?: { isVideo?: boolean; isDocument?: boolean; url?: string }[];
    }
  >
): PostType => {
  // MediaFiles could be (null, [], [{},...]). Shallow copy only: the array is
  // copied so the synthetic entry below can be pushed without touching the
  // caller's, and the elements are read-only from here on. Deep cloning would
  // also break callers that pass an immer draft.
  const mediaFiles: {
    originalUrl?: string;
    url?: string;
    isVideo?: boolean;
    isDocument?: boolean;
  }[] = Array.isArray(post.mediaFiles) ? [...post.mediaFiles] : [];

  // Annoyingly, if there is one asset it won't be in mediaFiles
  if (isEmpty(mediaFiles) && post.url) {
    mediaFiles.push({
      originalUrl: post.url,
      url: post.url,
      isVideo: post.isVideo,
      isDocument: post.isDocument,
    });
  }

  if (mediaFiles.length === 0) {
    return POST_TYPES.TEXT;
  }

  if (post.isStory) {
    return POST_TYPES.STORY;
  }

  if (isSingleVideo(mediaFiles, post)) {
    return POST_TYPES.VIDEO;
  }
  if (isSingleDocument(mediaFiles, post)) {
    return POST_TYPES.DOCUMENT;
  }

  if (isSingleImage(mediaFiles, post)) {
    return POST_TYPES.IMAGE;
  }

  return POST_TYPES.MULTI_MEDIA;
};

type PostToDetect = Partial<
  Pick<Post, "url" | "isVideo" | "isDocument" | "type" | "isStory">
>;

type MediaFileToDetect = {
  isVideo?: boolean;
  isDocument?: boolean;
  url?: string;
};

export const isSingleVideo = (
  mediaFiles: MediaFileToDetect[] | undefined,
  post: PostToDetect | undefined
) => {
  return (
    mediaFiles?.length === 1 &&
    (mediaFiles[0].isVideo === true || mediaFiles[0].url?.includes(".mp4")) &&
    !post?.isStory
  );
};
export const isSingleDocument = (
  mediaFiles: MediaFileToDetect[] | undefined,
  post: PostToDetect | undefined
) => {
  return (
    ((mediaFiles?.length === 1 && mediaFiles[0].isDocument === true) ||
      (mediaFiles?.length === 1 && mediaFiles[0].url?.includes(".pdf"))) &&
    !post?.isStory
  );
};
export const isSingleImage = (
  mediaFiles: MediaFileToDetect[] | undefined,
  post: PostToDetect | undefined
) => {
  return (
    mediaFiles?.length === 1 &&
    mediaFiles[0].isVideo !== true &&
    !isSingleDocument(mediaFiles, post) &&
    !isSingleVideo(mediaFiles, post) &&
    !post?.isStory
  );
};

const createDefaultValue = () => ({
  _id: NEW_POST_ID,
  type: "text" as const,
  when: null,
  newPost: true,
  caption: "",
  firstcomment: "",
  redirectUrl: null,
  tags: [] as unknown[],
  productTags: [] as unknown[],
  mediaFiles: [] as Post["mediaFiles"],
  media: [] as NonNullable<Post["media"]>,
  postStatusKey: null,
  unreadMessageCount: 0,
  preview_enabled: false,
  accountIds: [] as string[],
  accounts: [] as AccountType[],
  piBoards: [] as unknown[],
  piTitle: null,
  piSectionId: null,
  liTargetEntities: [] as LegacyAudienceMap[],
  labelIds: [] as string[],
  postOptions: {},
  videoCoverImageTimestamp: 0, // in ms
  videoCoverPreview: null,
  collaboratingIgUsers: [],
});

export const getNewPostObj = (initialData: Partial<Post> = {}): Post => {
  return {
    ...createDefaultValue(),
    ...initialData,
  } as Post;
};

const AUDIENCE_DATA = {
  companySize: companySizeData,
  staffCountRanges: companySizeData,
  seniorities: senioritiesData,
  seniority: senioritiesDataDeprecated,
  jobFunctions: jobFunctionsData,
  industries: industriesData,
};

/**
 *
 * @param {Object} post
 * @returns {Promise<Object>} new post object (not modify)
 */
export const standardizationPostData = async (post: Post) => {
  // eslint-disable-next-line unicorn/prefer-structured-clone
  const newPost = cloneDeep(post) as PostWithLegacyLinkedInAudiences;

  // NOTE: Old LI posts can has audiences on the old format
  // old variant liTargetEntities = [{"interfaceLocales": {"language": "en"}}, {"jobFunctions": ["urn:li:function:4"]}]
  // new variant liTargetEntities = {"interfaceLocales": {"language": "en"}, "jobFunctions": [{"id": "urn:li:function:4","label": "Business Development"}]}
  if (newPost.liTargetEntities && Array.isArray(newPost.liTargetEntities)) {
    const audienceEntities = newPost.liTargetEntities;
    newPost.liTargetEntities = await audienceEntities.reduce<
      Promise<AudienceMap>
    >(async (acc, audienceObj) => {
      const accResultObj = await acc;
      const audienceKey = Object.keys(audienceObj)[0];

      // ignore interfaceLocales (hidden property)
      if (audienceKey === "interfaceLocales") {
        return accResultObj;
      }

      accResultObj[audienceKey] = await Promise.all(
        (audienceObj[audienceKey] ?? []).map(async (audience) => {
          if (typeof audience === "string") {
            let label = "";
            switch (audienceKey) {
              case "geoLocations":
                label = await fetchLocationLabelById(audience.split(":")[3]);
                break;
              case "organizations": {
                const organizationData =
                  await fetchOrganizationLabelById(audience);
                label =
                  organizationData?.results?.[audience]?.localizedName ||
                  `Unknown organization (please reselect)`;

                break;
              }
              default:
                label =
                  (
                    AUDIENCE_DATA[
                      audienceKey as keyof typeof AUDIENCE_DATA
                    ]?.find(({ id }) => id === audience) || {}
                  ).label ?? "";
                break;
            }

            return { id: audience, label };
          }

          return isAudienceOption(audience) ? audience : { id: "", label: "" };
        })
      ).then((results) => results.filter((item) => item.id));

      return accResultObj;
    }, Promise.resolve({}));
  }

  const isFbReelPost = newPost.isFbReel || newPost.type === "fb-reel";
  if (isFbReelPost) {
    newPost.isFbReel = true;
    newPost.isVideo = true;
    newPost.videoPostType = "reel";
    newPost.type = "fb-reel";
  }
  if (newPost.mediaFiles) {
    /** deprecated, use mediaFiles instead */
    newPost.media = null;
  }

  return normalizePostMediaShape(newPost);
};

export const standardizationUpcomingPosts = async (
  posts: Post[],
  existing: Post[]
) => {
  return posts.reduce(
    async (acc, newPost) => {
      const newPosts = await acc;
      const postIsAlreadyInStore = existing.some(
        (existingPost) => existingPost._id === newPost._id
      );

      newPost.isVideo = isVideoPost(newPost);
      newPost.isDocument = isDocumentPost(newPost);

      if (!postIsAlreadyInStore) {
        const standardizedPostData = await standardizationPostData(newPost);
        newPosts.push(standardizedPostData);
      }

      return newPosts;
    },
    Promise.resolve([] as Post[])
  );
};

export function isNewPostId(postId: string | undefined) {
  return postId === NEW_POST_ID || postId?.startsWith("NEW_POST_");
}

export function generateNewPostId() {
  return `NEW_POST_${Math.random().toString(36).slice(2, 9)}`;
}

export function isVideoPost(post: Post) {
  return post.type === "video" || post.isVideo || post.type === "fb-reel";
}

export function isDocumentPost(post: Post) {
  return post.type === "document" || post.isDocument;
}

export function isLoadedDraft(post: Post | LoadedDraft): post is LoadedDraft {
  return !!(post as LoadedDraft).schedule;
}

export function extractCorePost(post: Post | LoadedDraft): Post {
  if (!post) {
    return post;
  }
  if (isLoadedDraft(post)) {
    return post.schedule;
  }
  return post;
}

export const getPostedState = (post: Post): "success" | "failed" | null => {
  const status = getPublishingStatus(post);
  if (status === "posted" || status === "imported") return "success";
  if (status === "failed") return "failed";
  return null;
};

export const resolveImgThumbnail = (
  post: Pick<
    Post,
    "thumbnailUrl" | "mediaFiles" | "media" | "url" | "origUrl" | "originalUrl"
  >
) => {
  return (
    pickThumbnailFallback({ thumbnailUrl: post?.thumbnailUrl }) ||
    pickThumbnailFallback(post?.mediaFiles?.[0]) ||
    pickThumbnailFallback(post?.media?.[0]) ||
    pickThumbnailFallback({
      url: post?.url,
      origUrl: post?.origUrl,
      originalUrl: post?.originalUrl,
    }) ||
    ""
  );
};
